import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({
    region: 'International',
    language: 'English',
    code: 'en'
  });

  // Language/Region options
  const languages = [
    { region: 'International', language: 'English', code: 'en' },
    { region: 'India', language: 'English', code: 'en-IN' },
    { region: 'India', language: 'हिन्दी', code: 'hi' },
    { region: 'United States', language: 'English', code: 'en-US' },
    { region: 'United Kingdom', language: 'English', code: 'en-GB' },
    { region: 'Canada', language: 'English', code: 'en-CA' },
    { region: 'Canada', language: 'Français', code: 'fr-CA' },
    { region: 'Australia', language: 'English', code: 'en-AU' },
    { region: 'Deutschland', language: 'Deutsch', code: 'de' },
    { region: 'France', language: 'Français', code: 'fr' },
    { region: 'España', language: 'Español', code: 'es' },
    { region: 'México', language: 'Español', code: 'es-MX' },
    { region: 'Brasil', language: 'Português', code: 'pt-BR' },
    { region: 'Portugal', language: 'Português', code: 'pt' },
    { region: 'Italia', language: 'Italiano', code: 'it' },
    { region: 'Nederland', language: 'Nederlands', code: 'nl' },
    { region: 'Россия', language: 'Русский', code: 'ru' },
    { region: '日本', language: '日本語', code: 'ja' },
    { region: '中国', language: '中文', code: 'zh' },
    { region: '대한민국', language: '한국어', code: 'ko' },
    { region: 'العربية', language: 'العربية', code: 'ar' },
    { region: 'Singapore', language: 'English', code: 'en-SG' },
    { region: 'Malaysia', language: 'English', code: 'en-MY' },
    { region: 'Thailand', language: 'English', code: 'en-TH' },
    { region: 'Indonesia', language: 'Bahasa', code: 'id' },
    { region: 'South Africa', language: 'English', code: 'en-ZA' },
    { region: 'UAE', language: 'English', code: 'en-AE' },
    { region: 'Saudi Arabia', language: 'العربية', code: 'ar-SA' },
  ];

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLanguageSelect = (lang) => {
    setCurrentLanguage(lang);
    setIsOpen(false);
    
    // Change the language using i18n
    i18n.changeLanguage(lang.code);
    
    // Save to localStorage
    localStorage.setItem('selectedLanguage', JSON.stringify(lang));
    
    // Set document direction for RTL languages (Arabic)
    if (lang.code === 'ar' || lang.code === 'ar-SA') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = lang.code;
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = lang.code;
    }
  };

  // Load saved language on mount and sync with i18n
  useEffect(() => {
    const saved = localStorage.getItem('selectedLanguage');
    if (saved) {
      try {
        const parsedLang = JSON.parse(saved);
        setCurrentLanguage(parsedLang);
        i18n.changeLanguage(parsedLang.code);
        
        // Set document direction for RTL languages
        if (parsedLang.code === 'ar' || parsedLang.code === 'ar-SA') {
          document.documentElement.dir = 'rtl';
        }
        document.documentElement.lang = parsedLang.code;
      } catch (e) {
        console.error('Error loading saved language');
      }
    }
  }, [i18n]);

  // Globe icon
  const GlobeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="2" y1="12" x2="22" y2="12"></line>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
    </svg>
  );

  // Close icon
  const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  return (
    <>
      {/* Language Selector Button */}
      <button 
        className="language-selector-btn"
        onClick={() => setIsOpen(true)}
        aria-label="Select language and region"
      >
        <GlobeIcon />
        <span className="language-text">
          {currentLanguage.region} – {currentLanguage.language}
        </span>
      </button>

      {/* Language Modal */}
      {isOpen && (
        <div className="language-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="language-modal" onClick={(e) => e.stopPropagation()}>
            <div className="language-modal-header">
              <h2>{t('language.chooseLocation')}</h2>
              <button 
                className="language-modal-close" 
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            
            <div className="language-modal-content">
              <div className="language-grid">
                {languages.map((lang, index) => (
                  <button
                    key={`${lang.code}-${index}`}
                    className={`language-option ${currentLanguage.code === lang.code && currentLanguage.region === lang.region ? 'active' : ''}`}
                    onClick={() => handleLanguageSelect(lang)}
                  >
                    <span className="language-option-region">{lang.region}</span>
                    <span className="language-option-separator"> – </span>
                    <span className="language-option-lang">{lang.language}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="language-modal-footer">
              <p>{t('language.cantFind')}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LanguageSelector;
